import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

import { postRequest, getRequest } from 'utils/api';
import { setGig } from 'features/gigsSlice';
import { formatPrice } from 'utils/helpers';
import toast from 'utils/toast.jsx';
import IconMainContainer from 'components/IconMainContainer';

const containerStyle = { margin: '15px' };
const buttonStyle = { marginTop: 10 };

const PaymentChargeBanner = ({ milestone, gig, inline }) => {
  const dispatch = useDispatch();
  const [isRetrying, setIsRetrying] = useState(false);
  const charge = milestone.payment_charge;

  if (milestone.status !== 'approve_attempting_payment' || !charge) return null;

  const failed = charge.status === 'failed';
  const canRetry = failed && charge.retryable;
  const severity = failed ? 'error' : 'info';
  const amount = charge.amount ? formatPrice(charge.amount) : null;
  const title = failed ? 'Payment failed' : 'Payment processing';
  const failedMessage = charge.retryable
    ? 'Payment failed after several attempts — please fund your card and retry.'
    : 'Payment failed after several attempts. Please contact support for help completing this payment.';
  const message = failed
    ? failedMessage
    : `Payment processing — we'll charge your card automatically. Make sure it's funded.${
        amount ? ` Amount due: ${amount}.` : ''
      }`;

  const refreshGig = () =>
    getRequest({ endpoint: `gigs/${gig.id}` })
      .then(updated => {
        if (updated) dispatch(setGig(updated));
      })
      .catch(() => {});

  const handleRetry = e => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsRetrying(true);

    postRequest({
      endpoint: `payment_charges/${charge.id}/retry`,
      data: {},
    })
      .then(() => {
        toast('success', "Retry submitted — we're processing your payment.", {
          icon: <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />,
        });
        // Reset before refreshGig, which may change the milestone status and
        // unmount this banner — avoids a setState-after-unmount warning.
        setIsRetrying(false);
        return refreshGig();
      })
      .catch(() => setIsRetrying(false));
  };

  return (
    <div style={inline ? { margin: '8px 0' } : containerStyle} onClick={e => e.stopPropagation()}>
      <Alert severity={severity}>
        <AlertTitle>{title}</AlertTitle>
        {message}
        {canRetry && (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={buttonStyle}
              disabled={isRetrying}
              onClick={handleRetry}
            >
              {isRetrying ? 'Retrying…' : 'Retry payment'}
            </Button>
          </div>
        )}
      </Alert>
    </div>
  );
};

PaymentChargeBanner.propTypes = {
  milestone: PropTypes.object.isRequired,
  gig: PropTypes.object.isRequired,
  inline: PropTypes.bool,
};

PaymentChargeBanner.defaultProps = {
  inline: false,
};

export default PaymentChargeBanner;
